/* eslint-disable no-unused-vars */
import React, { useEffect, useState, useCallback } from "react";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { Route, Switch, useHistory, Redirect } from "react-router-dom";
import PrivateRoute from "./PrivateRoute";
import {
  Body,
  Footer,
  Layout,
  Loader,
  usePrivacyPolicyBenchmarking,
  User,
  httpClient,
  memberTypeRepository,
  init as initI18n,
  getBrowserLanguageKey,
  useTranslator,
  getLocale,
  usePathFactory,
} from "@sia/style-guide";
import "@sia/style-guide/dist/base.css";
import HttpErrorContextProvider from "contexts/HttpErrorContext";
import GlobalContext from "contexts/GlobalContext";
import MasterKeyProvider from "contexts/MasterKeyContext";
import { DataCollectionProvider } from "contexts/DataCollectionContext";
import KeycloakIDPAdapter from "common/Adapters/IDPAdapter/KeycloakIDPAdapter";
import { useFooterContents } from "common/Layout/FooterContents";
import CookieClient from "components/CookieClient";
import HeaderClient from "components/HeaderClient";
import NotFoundPage from "pages/Error/NotFoundPage";
import ErrorPage from "pages/Error/ErrorPage";
import SurveyPage from "pages/SurveyPage";
import webPlatformAdapter from "common/Adapters/WebPlatformAdapter/WebPlatformAdapterAsync";
import cmsAdapter from "./common/Adapters/CMSAdapter/CMSAdapter";
import CMSContent from "pages/CMSContent/CMSContent";
import HomePage from "pages/HomePage";
import WithLoader from "common/HOCs/WithLoader";
import { useSurvey } from "./pages/SurveyPage/useSurvey";
import EvaluationsRoutes from "pages/Evaluations/routes";
// import CalculationsRoute from "pages/Calculation/CalculationsRoute";

initI18n();
/* eslint-disable react/prop-types */
function App({ isLoaderShown, setIsLoaderShown }) {
  const { i18n } = useTranslation();
  const translate = useTranslator();
  const history = useHistory();
  const pathFactory = usePathFactory();
  const footerContents = useFooterContents();
  useEffect(() => {
    httpClient.setLoaderStateCallback(setIsLoaderShown);
    httpClient.setGlobalLoaderStateCallback(setIsLoaderShown);
  }, [setIsLoaderShown]);
  const [menuItems, setMenuItems] = useState(process.env.REACT_APP_CMS_URL ? [] : undefined);
  const [noMenuPages, setNoMenuPages] = useState(process.env.REACT_APP_CMS_URL ? [] : undefined);
  const [isLoadingPages, setIsLoadingPages] = useState(process.env.REACT_APP_CMS_URL ? true : false);
  const [user, setUser] = useState(null);
  const [headerUser, setHeaderUser] = useState(null);
  const [isIDPInitialized, setIsIDPInitialized] = useState(false);
  const [wasIDPInitializationStarted, setWasIDPInitializationStarted] = useState(false);
  const [, setIsSubscribedToAnyService] = useState(false);
  const [loginUrl, setLoginUrl] = useState("");

  const { getSurvey } = useSurvey();
  useEffect(() => {
    if (!survey.id) getSurvey(setGlobalSurvey);
  }, []);

  const { PrivacyPolicyComponent, handlePrivacyPolicyNotification } = usePrivacyPolicyBenchmarking({
    webPlatformAdapter,
    getLocale,
    translate,
    pathFactory,
    lang: i18n.language,
  });
  
  const [survey, setGlobalSurvey] = useState({
    id: null,
    surveyYear: null,
    initializedAt: null,
    activatedAt: null,
    finishedAt: null,
    toClose: null,
    publishedAt: null,
    inactivatedAt: null,
  });

  const localStorageLanguageKey = sessionStorage.getItem("sia.benchmarking.languageKey");
  const globalContextValue = {
    isLoaderShown,
    setIsLoaderShown,
    isIDPInitialized: isIDPInitialized,
    user: user,
    language: i18n.language,
    menuItems,
    setMenuItems,
    noMenuPages,
    setGlobalSurvey,
    survey,
    loginUrl,
  };
  const getUrlLanguageKey = useCallback(() => {
    const pathSplit = history.location.pathname.split("/");
    return pathSplit[1];
  }, [history.location.pathname]);

  const isValidLanguageKey = useCallback((languageKey) => Object.keys(i18n.options.resources).includes(languageKey), [i18n.options.resources]);

  const getApplyingLanguage = useCallback(() => {
    const urlLanguageKey = getUrlLanguageKey();
    const validUrlLanguageKey = isValidLanguageKey(urlLanguageKey) ? urlLanguageKey : null;
    if (validUrlLanguageKey) {
      return validUrlLanguageKey;
    }
    if (localStorageLanguageKey) {
      return localStorageLanguageKey;
    }
    return getBrowserLanguageKey();
  }, [localStorageLanguageKey, getUrlLanguageKey, isValidLanguageKey]);

  const createHeaderUser = (user, customerData, originalMemberType) => {
    const id = parseFloat(user.getSiaId());
    if (user.isMemberTypeGroupOf("single")) {
      return {
        id: id,
        name: [customerData.firstname, customerData.lastname].join(" "),
        type: originalMemberType,
      };
    }
    if (user.isMemberTypeGroupOf("corporate")) {
      return {
        id: id,
        name: customerData.name,
        additionalName: customerData.additional_name,
        type: originalMemberType,
      };
    }
  };

  const renderPage = (component) => {
    const urlLanguageKey = getUrlLanguageKey();
    if (urlLanguageKey && !isValidLanguageKey(urlLanguageKey)) {
      return <NotFoundPage />;
    }
    return component;
  };

  useEffect(() => {
    if (wasIDPInitializationStarted) {
      return;
    }
    i18n.changeLanguage(getApplyingLanguage(), () => {
      process.env.REACT_APP_CMS_URL && loadMenu(i18n.language);
    });
    setIsLoaderShown(true);
    setWasIDPInitializationStarted(true);
    KeycloakIDPAdapter.initBM(async (idpUser) => {
      setIsIDPInitialized(true);
      setTimeout(() => {
        setIsLoaderShown(false);
      }, 1200);
      if (idpUser) {
        if (sessionStorage.getItem("sia.benchmarking.forceUserLanguage")) {
          sessionStorage.removeItem("sia.benchmarking.forceUserLanguage");
          sessionStorage.setItem("sia.benchmarking.languageKey", idpUser.getLocale());
          const languageKeyReplacedUrl = createLanguageKeyReplacedUrl(idpUser.getLocale());
          window.location.replace(languageKeyReplacedUrl);
          return;
        }

        const servicesRoles = ["sia_role_member_corporate", "sia_role_member_single"];
        if (servicesRoles.includes(idpUser.role)) {
          setIsSubscribedToAnyService(true);
        }

        const data = await webPlatformAdapter.getLoginInformation(i18n.language);
        const originalMemberType = data[0].member_type.name;
        const memberType = memberTypeRepository.getById(data[0].member_type.id);
        const hasSubsidiaries = data[0].has_subsidiaries;
        const user = new User(idpUser.getUsername(), memberType, idpUser.getLocale(), idpUser.getRole(), originalMemberType, hasSubsidiaries);
        const data_customer = await webPlatformAdapter.getCustomerData(user);
        const customerData = data_customer[0];
        setHeaderUser(createHeaderUser(user, customerData, originalMemberType));
        setUser(user);

        handlePrivacyPolicyNotification();
      } else {
        setLoginUrl(KeycloakIDPAdapter.createLoginUrl(i18n.language, null, true));
      }
    });
  }, [wasIDPInitializationStarted, i18n, getApplyingLanguage, translate, loadMenu]);

  const createLanguageKeyReplacedUrl = (languageKey) => {
    let pathParts = window.location.pathname.split("/");
    pathParts[1] = languageKey;
    const hash = window.location.hash ? window.location.hash : "";
    const search = window.location.search ? window.location.search : "";
    return window.location.origin + pathParts.join("/") + search + hash;
  };

  async function loadMenu(lang) {
    const data = await cmsAdapter.fetchMenuItemsBenchmarking(lang, history);
    setMenuItems(data.menuItems);
    loadNoMenu(lang);
  }

  async function loadNoMenu(lang) {
    const data = await cmsAdapter.fetchNoMenuV2(lang);
    setNoMenuPages(data);
    setIsLoadingPages(false);
  }

  return (
    <>
      <HelmetProvider>
        <Helmet>
          <html lang={i18n.language} />
        </Helmet>
      </HelmetProvider>
      {isLoaderShown && <Loader />}
      <Layout>
        {
          <HeaderClient
            pathFactory={pathFactory}
            history={history}
            user={user}
            menuItems={menuItems}
            i18n={i18n}
            createLanguageKeyReplacedUrl={createLanguageKeyReplacedUrl}
            translate={translate}
            headerUser={headerUser}
            defaultAppSwitcherValue={""}
          />
        }
        <Body>
          <GlobalContext.Provider
            // @ts-ignore
            value={globalContextValue}
          >
            <HttpErrorContextProvider>
              <MasterKeyProvider>
                <Switch>
                  <Route exact path="/" component={() => <Redirect to={`/${getApplyingLanguage()}`} />} />
                  <Route exact path="/:languageKey" render={() => renderPage(<HomePage />)} />
                  {/* @ts-ignore */}
                  {!isLoadingPages && <Route path="/:languageKey/cms/:alias" render={() => renderPage(<CMSContent />)} />}
                  {/* @ts-ignore */}
                  {!isLoadingPages && <Route path="/:languageKey/cms/hash/:alias" render={() => renderPage(<CMSContent isHash />)} />}
                  {noMenuPages?.map((page) => {
                    return (
                      <Route
                        key={page.id}
                        path={`/:languageKey/cms${page.alias}`}
                        /* @ts-ignore */
                        render={() => renderPage(<CMSContent nodeId={page.id} />)}
                      />
                    );
                  })}
                  <Route path="/:languageKey/error" component={ErrorPage} />

                  <PrivateRoute
                    exact
                    path="/:languageKey/survey"
                    masterKeyIsValid
                    render={() =>
                      renderPage(
                        <DataCollectionProvider>
                          <SurveyPage />
                        </DataCollectionProvider>
                      )
                    }
                  />

                  {/* <Route path="/:languageKey/stats/calculations" render={() => <CalculationsRoute />} /> */}
                  <Route path="/:languageKey/stats" render={() => <EvaluationsRoutes />} />
                  <Route path="*" component={isLoadingPages ? () => <></> : NotFoundPage} />
                </Switch>
              </MasterKeyProvider>
            </HttpErrorContextProvider>
          </GlobalContext.Provider>
          <CookieClient history={history} />
        </Body>
        <Footer
          follow={footerContents.follow}
          contactUs={footerContents.contactUs}
          links={footerContents.links}
          about={footerContents.about}
          translate={translate}
        />
      </Layout>
      <PrivacyPolicyComponent />
    </>
  );
}

export default WithLoader(App);
